// Inbound triage — columnar table (Fig59 / image23). Columns: Applicant /
// Matched venture / Sector / Match score / Received / Status. Every cell is
// driven straight off the real AggregatorProject record returned by
// GET /aggregator/portfolio-projects — no column here is backed by a field
// that doesn't exist on that record (P7: never fabricate a row or a status).
import { useEffect, useRef } from 'react'
import { Send } from 'lucide-react'
import type { AggregatorProject } from '../../types'

// ---------------------------------------------------------------------------
// Pure, exported helpers — unit-testable without rendering anything.
// ---------------------------------------------------------------------------

// Real score → label. null/undefined means "not yet scored" — an honest em
// dash, never a fabricated percentage.
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—'
  return `${Math.round(score * 100)}%`
}

export function scoreBarColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'bg-gray-200'
  if (score >= 0.8) return 'bg-emerald-500'
  if (score >= 0.6) return 'bg-iflow-500'
  if (score >= 0.4) return 'bg-amber-400'
  return 'bg-gray-300'
}

// match_run_status is written by crosspollination/handler.py — real values are
// 'pending' | 'in_progress' | 'complete' | 'failed' | 'not_ready'. A row with
// no value at all (scoring hasn't been scheduled yet) gets an honest
// "Unscored" label, never a fabricated "New"/"Viewed" read-state that has no
// backing field in AggregatorProject.
export const STATUS_UNSCORED = '__unscored__'

export function matchStatusMeta(status: string | null | undefined): { key: string; label: string; className: string } {
  switch (status) {
    case 'complete':
      return { key: 'complete', label: 'Scored', className: 'bg-emerald-100 text-emerald-700' }
    case 'in_progress':
      return { key: 'in_progress', label: 'Scoring…', className: 'bg-sky-100 text-sky-700' }
    case 'pending':
      return { key: 'pending', label: 'Queued', className: 'bg-slate-100 text-slate-600' }
    case 'failed':
      return { key: 'failed', label: 'Failed', className: 'bg-rose-100 text-rose-700' }
    case 'not_ready':
      return { key: 'not_ready', label: 'Not ready', className: 'bg-amber-100 text-amber-800' }
    default:
      return { key: STATUS_UNSCORED, label: 'Unscored', className: 'bg-slate-100 text-slate-500' }
  }
}

// Real elapsed-time label computed from the real created_at timestamp — never
// a fabricated "2h ago", just real arithmetic against the current clock.
export function formatReceived(createdAt: string | undefined): string {
  if (!createdAt) return '—'
  const created = new Date(createdAt).getTime()
  if (Number.isNaN(created)) return '—'
  const diffMs = Date.now() - created
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

interface SelectAllCheckboxProps {
  checked: boolean
  indeterminate: boolean
  onChange: () => void
}

function SelectAllCheckbox({ checked, indeterminate, onChange }: SelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      data-testid="inbound-select-all-checkbox"
      checked={checked}
      onChange={onChange}
      aria-label="Select all visible rows"
      className="h-4 w-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500 cursor-pointer"
    />
  )
}

export interface InboundTriageTableProps {
  rows: AggregatorProject[]
  selectedIds: Set<string>
  onToggleRow: (id: string) => void
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
  onOpenRow: (id: string) => void
  onInviteRow: (project: AggregatorProject) => void
  canInvite: boolean
}

export default function InboundTriageTable({
  rows,
  selectedIds,
  onToggleRow,
  allSelected,
  someSelected,
  onToggleAll,
  onOpenRow,
  onInviteRow,
  canInvite,
}: InboundTriageTableProps) {
  return (
    <div className="light-card overflow-x-auto" data-testid="inbound-triage-table">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-iflow-50/60 text-left text-xs font-semibold uppercase tracking-wide text-iflow-900">
            <th className="w-10 px-4 py-3">
              <SelectAllCheckbox checked={allSelected} indeterminate={!allSelected && someSelected} onChange={onToggleAll} />
            </th>
            <th className="px-4 py-3">Applicant</th>
            <th className="px-4 py-3">Matched venture</th>
            <th className="px-4 py-3">Sector</th>
            <th className="px-4 py-3">Match score</th>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((p) => {
            const score = p.top_match_score ?? null
            const pct = score !== null ? Math.round(score * 100) : 0
            const checked = selectedIds.has(p.project_id)
            const status = matchStatusMeta(p.match_run_status)
            return (
              <tr
                key={p.project_id}
                data-testid={`inbound-row-${p.project_id}`}
                className={`hover:bg-gray-50 ${checked ? 'bg-iflow-50/50' : ''}`}
              >
                <td className="px-4 py-3 align-top">
                  <input
                    type="checkbox"
                    data-testid={`inbound-row-checkbox-${p.project_id}`}
                    checked={checked}
                    onChange={() => onToggleRow(p.project_id)}
                    aria-label={`Select disclosure from ${p.entity_name}`}
                    className="h-4 w-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <span className="font-medium text-gray-900" data-testid={`inbound-applicant-${p.project_id}`}>
                    {p.entity_name}
                  </span>
                </td>
                <td className="px-4 py-3 align-top">
                  <button
                    onClick={() => onOpenRow(p.project_id)}
                    className="text-left text-iflow-700 hover:text-iflow-800 hover:underline"
                    data-testid={`inbound-venture-${p.project_id}`}
                  >
                    {p.name}
                  </button>
                </td>
                <td className="px-4 py-3 align-top text-gray-600">
                  {p.vertical ?? <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2">
                    {/* Bug #5898 (F18): a null score collapsed to a 0%-width
                        bar plus a bare "—", which read as a blank row with no
                        percentage, bar, or label at all — not the honest
                        "not yet scored" state it was meant to be. Skip the bar
                        entirely and say so in words instead of a near-invisible
                        empty state. */}
                    {score === null ? (
                      <span
                        className="whitespace-nowrap text-xs font-medium text-gray-400"
                        data-testid={`inbound-score-${p.project_id}`}
                      >
                        Not yet scored
                      </span>
                    ) : (
                      <>
                        <div className="h-2 w-20 shrink-0 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                          <div className={`h-full rounded-full ${scoreBarColor(score)}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span
                          className="w-10 shrink-0 text-right font-semibold tabular-nums text-gray-800"
                          data-testid={`inbound-score-${p.project_id}`}
                        >
                          {formatScore(score)}
                        </span>
                      </>
                    )}
                    {typeof p.match_count === 'number' && p.match_count > 0 && (
                      <span className="whitespace-nowrap text-xs text-gray-500">
                        {p.match_count} match{p.match_count === 1 ? '' : 'es'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 align-top text-gray-600" data-testid={`inbound-received-${p.project_id}`}>
                  {formatReceived(p.created_at)}
                </td>
                <td className="px-4 py-3 align-top">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}
                    data-testid={`inbound-status-${p.project_id}`}
                  >
                    {status.label}
                  </span>
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <button
                    onClick={() => onInviteRow(p)}
                    disabled={!canInvite}
                    data-testid={`inbound-row-invite-${p.project_id}`}
                    title={canInvite ? undefined : 'No active aggregator selected'}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send size={12} /> Invite
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
